import { ConfigProvider, Segmented } from "antd";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import "./PortfolioPage.scss";

import { PlusOutlined } from "@ant-design/icons";
import moment from "moment";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { createAgencyUser, updatePortfolio } from "../../assets/api/agency";
import { downloadPortfolioFile, uploadCSV } from "../../assets/api/portfolio";
import { ReactComponent as ListIcon } from "../../assets/Icons/portfolioTab1.svg";
import { ReactComponent as GridIcon } from "../../assets/Icons/portfolioTab2.svg";
import PortfolioCards from "../../components/PortfolioComp/PortfolioCards/PortfolioCards";
import PortfolioModal from "../../components/PortfolioComp/PortfolioModal/PortfolioModal";
import PortfolioTable from "../../components/PortfolioComp/PortfolioTable/PortfolioTable";
import CustomNotification from "../../Reuseable/CustomNotification/CustomNotification";
import NormalButton from "../../Reuseable/NormalButton/NormalButton";
import { fetchPortfolios } from "../../store/portfolioSlice";
import { ExportDateWiseModal } from "../../components/ExportModal/ExportModal";

const PortfolioPage = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [viewType, setViewType] = useState("grid");
  const { token } = useSelector((state) => state.auth);
  const [openDatewiseExportModal, setOpenDatewiseExportModal] = useState(false);
  const [selectedPortfolio, setSelectedPortfolio] = useState(null);
  const [loadingExport, setLoadingExport] = useState(null);
  const [load, setLoad] = useState(false);
  const navigate = useNavigate();

  const { portfolios, loading } = useSelector((state) => state?.portfolio);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(fetchPortfolios());
  }, [dispatch]);

  const handleCreatePortfolio = async (user) => {
    const { name, acquiredOn, total_tos, totalPOS, totalCases } = user;
    let res;
    setLoad(true);
    try {
      if (user?.is_edit) {
        res = await updatePortfolio(
          {
            payload: {
              name,
              acquired_on: dayjs(acquiredOn).format("YYYY-MM-DD"),
              total_cases: totalCases,
              total_pos: totalPOS,
              total_tos: total_tos,
            },
            portfolio_id: user?.id,
          },
          token,
        );

        if (res?.status === 200) {
          toast.success("Portfolio updated");
          if (file !== null) {
            await uploadCSV(
              {
                portfolio: user?.id,
                file: file?.[0]?.originFileObj,
              },
              token,
            );
          }
          dispatch(fetchPortfolios());
          setIsEditModalOpen(false);
        }
      } else {
        res = await createAgencyUser(
          {
            name,
            acquired_on: moment(acquiredOn).format("YYYY-MM-DD"),
            total_cases: totalCases,
            total_pos: totalPOS,
            total_tos: total_tos,
          },
          token,
        );

        if (res?.status === 201) {
          const portfolioId = res?.data?.data?.portfolio?.id;
          const portfolioName = res?.data?.data?.portfolio?.name;

          if (file !== null) {
            await uploadCSV(
              {
                portfolio: portfolioId,
                file: file?.[0]?.originFileObj,
              },
              token,
            );
          }

          toast.success(res?.data?.message);
          dispatch(fetchPortfolios());
          setIsModalOpen(false);
        }
      }
    } catch (error) {
      if (error?.message === "Headers did not match master headers.") {
        const portfolioName = res?.data?.data?.portfolio?.name || user?.name;
        const portfolioId = res?.data?.data?.portfolio?.id || user?.id;

        navigate(`/portfolio/${portfolioName}`, {
          state: { id: portfolioId },
        });
      } else {
        CustomNotification.show(error?.message, "error");
      }
    } finally {
      setLoad(false);
    }
  };

  const showModal = () => {
    setIsModalOpen(true);
  };

  const options = [
    {
      value: "list",
      label: <ListIcon className="segmented-icon" />,
    },
    {
      value: "grid",
      label: <GridIcon className="segmented-icon" />,
    },
  ];

  const handleExport = async (params = {}) => {
    try {
      setLoadingExport(selectedPortfolio.id);

      const res = await downloadPortfolioFile(token, {
        ...params,
        portfolio_id: selectedPortfolio?.id,
      });

      if (res?.status === 200) {
        const downloadUrl = res?.data?.data?.download_url;

        if (downloadUrl) {
          // 🔽 Trigger browser download
          const link = document.createElement("a");
          link.href = downloadUrl;
          link.setAttribute("download", ""); // optional, browser decides filename
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      } else {
        toast.error(res?.message || "Failed to export cases");
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingExport(null);
    }
  };

  return (
    <div className="portfolioPageMainWrapper">
      <div className="portfolioPageMain">
        <div className="portfolioPageMainHeader">All Portfolios</div>
        <div className="portfolioPageMainSegment">
          <ConfigProvider
            theme={{
              components: {
                Segmented: {
                  itemSelectedBg: "rgba(0, 127, 255, 0.10)",
                  trackBg: "#fff",
                  trackPadding: "2px",
                },
              },
            }}
          >
            <Segmented
              options={options}
              value={viewType}
              onChange={setViewType}
              className="custom-segmented"
            />
          </ConfigProvider>
          <NormalButton
            className="addPortfolioBtn"
            onClick={showModal}
            type="primary"
            suffixIcon={<PlusOutlined />}
          >
            Add Portfolio
          </NormalButton>
        </div>
      </div>

      {/* Show modal if open */}
      <PortfolioModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleCreatePortfolio}
        mode="add"
        load={load}
        setFile={setFile}
        file={file}
      />
      <div className="portfolioContent">
        {viewType === "list" ? (
          <PortfolioTable
            setSelectedItem={setSelectedPortfolio}
            setOpenDatewiseExportModal={setOpenDatewiseExportModal}
            file={file}
            data={loading ? Array(5).fill({}) : portfolios}
            loading={loading}
            setFile={setFile}
            handleCreatePortfolio={handleCreatePortfolio}
            load={load}
            isEditModalOpen={isEditModalOpen}
            setIsEditModalOpen={setIsEditModalOpen}
          />
        ) : (
          <PortfolioCards
            setSelectedItem={setSelectedPortfolio}
            setOpenDatewiseExportModal={setOpenDatewiseExportModal}
            file={file}
            data={loading ? Array(5).fill({}) : portfolios}
            loading={loading}
            setFile={setFile}
            handleCreatePortfolio={handleCreatePortfolio}
            load={load}
            isEditModalOpen={isEditModalOpen}
            setIsEditModalOpen={setIsEditModalOpen}
          />
        )}
      </div>
      <ExportDateWiseModal
        loadingExport={loadingExport}
        data={selectedPortfolio}
        open={openDatewiseExportModal}
        setOpen={setOpenDatewiseExportModal}
        handleExport={handleExport}
      />
    </div>
  );
};

export default PortfolioPage;
